import React, { ReactNode, useEffect, useReducer } from 'react';
import {
  Button, ButtonToolbar, Form, IconButton, Loader, Stack,
} from 'rsuite';
import EditIcon from '@rsuite/icons/Edit';
import { SelectOptionWithActions, SelectPickerWithActions } from '../../SelectPickerWithActions';
import { Eshop, getPartnerEshopResponsibilities, savePartnerEshopResponsibilities } from '../../../api/eshop/eshop';
import { PartnerEshopResponsibilities } from '../../../api/apiTypes';
import { AllegroResponsiblePerson } from '../../../api/allegro/responsibleperson/responsiblePersonTypes';
import { AllegroResponsibleProducer } from '../../../api/allegro/responsibleproducer/responsibleProducerTypes';
import EshopSetupAllegroResponsiblePersonEditModal
  from './ResponsiblePerson/EshopSetupAllegroResponsiblePersonEditModal';
import EshopSetupAllegroResponsibleProducerEditModal
  from './ResponsibleProducer/EshopSetupAllegroResponsibleProducerEditModal';
import FilePicker from '../../FilePicker';
import { showSuccessMessage } from '../../../utils/message';
import downloadResponsiblePersonsExcel
  from '../../../services/allegro/responsibleperson/downloadResponsiblePersonsExcel';
import fetchAllegroResponsiblePersons from '../../../services/allegro/responsibleperson/fetchAllegroResponsiblePersons';
import uploadResponsiblePersonsExcel from '../../../services/allegro/responsibleperson/uploadResponsiblePersonsExcel';
import downloadResponsibleProducersExcel
  from '../../../services/allegro/responsibleproducer/downloadResponsibleProducersExcel';
import fetchAllegroResponsibleProducers
  from '../../../services/allegro/responsibleproducer/fetchAllegroResponsibleProducers';
import uploadResponsibleProducersExcel
  from '../../../services/allegro/responsibleproducer/uploadResponsibleProducersExcel';

interface Props {
  eshop: Eshop;
}

type Status = 'ready' | 'loading' | 'saving';

interface Edit {
  responsiblePersonId: string | null;
  responsibleProducerId: string | null;
}

interface ResponsiblePersonsState {
  data: AllegroResponsiblePerson[];
  isEditing: boolean;
  editingResponsiblePerson?: AllegroResponsiblePerson;
}

interface ResponsibleProducersState {
  data: AllegroResponsibleProducer[];
  isEditing: boolean;
  editingResponsibleProducer?: AllegroResponsibleProducer;
}

interface State {
  status: Status;
  edit: Edit;
  responsibilities?: PartnerEshopResponsibilities;
  responsiblePersons: ResponsiblePersonsState;
  responsibleProducers: ResponsibleProducersState;
}

const initialState: State = {
  status: 'loading',
  edit: {
    responsiblePersonId: null,
    responsibleProducerId: null,
  },
  responsibilities: undefined,
  responsiblePersons: {
    data: [],
    isEditing: false,
    editingResponsiblePerson: undefined,
  },
  responsibleProducers: {
    data: [],
    isEditing: false,
    editingResponsibleProducer: undefined,
  },
};

type StatusSetAction = {
  type: 'status/set',
  payload: Status
};
type EditSetAction = {
  type: 'edit/set',
  payload: Edit
};
type EditCancelAction = {
  type: 'edit/cancel'
};
type ResponsibilitiesSetAction = {
  type: 'responsibilities/set',
  payload?: PartnerEshopResponsibilities
};
type ResponsiblePersonsDataSetAction = {
  type: 'responsiblePersons/data/set',
  payload: AllegroResponsiblePerson[]
};
type ResponsiblePersonsEditOpenAction = {
  type: 'responsiblePersons/edit/open',
  payload?: AllegroResponsiblePerson
};
type ResponsiblePersonsEditCloseAction = {
  type: 'responsiblePersons/edit/close'
};
type ResponsibleProducersDataSetAction = {
  type: 'responsibleProducers/data/set',
  payload: AllegroResponsibleProducer[]
};
type ResponsibleProducersEditOpenAction = {
  type: 'responsibleProducers/edit/open',
  payload?: AllegroResponsibleProducer
};
type ResponsibleProducersEditCloseAction = {
  type: 'responsibleProducers/edit/close'
};
type Action = StatusSetAction
| EditSetAction
| EditCancelAction
| ResponsibilitiesSetAction
| ResponsiblePersonsDataSetAction
| ResponsiblePersonsEditOpenAction
| ResponsiblePersonsEditCloseAction
| ResponsibleProducersDataSetAction
| ResponsibleProducersEditOpenAction
| ResponsibleProducersEditCloseAction;

function reducer(state: State, action: Action): State {
  switch (action.type) {
    case 'status/set': {
      return { ...state, status: action.payload };
    }
    case 'edit/set': {
      return { ...state, edit: action.payload };
    }
    case 'edit/cancel': {
      return {
        ...state,
        edit: {
          responsiblePersonId: state.responsibilities?.defaultResponsiblePersonId ?? null,
          responsibleProducerId: state.responsibilities?.defaultResponsibleProducerId ?? null,
        },
      };
    }
    case 'responsibilities/set': {
      return {
        ...state,
        responsibilities: action.payload,
        edit: {
          responsiblePersonId: action.payload?.defaultResponsiblePersonId ?? null,
          responsibleProducerId: action.payload?.defaultResponsibleProducerId ?? null,
        },
      };
    }
    case 'responsiblePersons/data/set': {
      return {
        ...state,
        responsiblePersons: {
          ...state.responsiblePersons,
          data: action.payload,
        },
      };
    }
    case 'responsiblePersons/edit/open': {
      return {
        ...state,
        responsiblePersons: {
          ...state.responsiblePersons,
          isEditing: true,
          editingResponsiblePerson: action.payload,
        },
      };
    }
    case 'responsiblePersons/edit/close': {
      return {
        ...state,
        responsiblePersons: {
          ...state.responsiblePersons,
          isEditing: false,
          editingResponsiblePerson: undefined,
        },
      };
    }
    case 'responsibleProducers/data/set': {
      return {
        ...state,
        responsibleProducers: {
          ...state.responsibleProducers,
          data: action.payload,
        },
      };
    }
    case 'responsibleProducers/edit/open': {
      return {
        ...state,
        responsibleProducers: {
          ...state.responsibleProducers,
          isEditing: true,
          editingResponsibleProducer: action.payload,
        },
      };
    }
    case 'responsibleProducers/edit/close': {
      return {
        ...state,
        responsibleProducers: {
          ...state.responsibleProducers,
          isEditing: false,
          editingResponsibleProducer: undefined,
        },
      };
    }
    default:
      return state;
  }
}

export default function EshopSetupAllegroResponsibility({ eshop }: Props): JSX.Element {
  const [state, dispatch] = useReducer(reducer, initialState);
  const marketplaceCode = eshop.code;

  async function fetchData() {
    dispatch({ type: 'status/set', payload: 'loading' });

    try {
      const [responsibilities, responsiblePersons, responsibleProducers] = await Promise.all([
        getPartnerEshopResponsibilities(marketplaceCode),
        fetchAllegroResponsiblePersons(marketplaceCode),
        fetchAllegroResponsibleProducers(marketplaceCode),
      ]);

      dispatch({ type: 'responsiblePersons/data/set', payload: responsiblePersons });
      dispatch({ type: 'responsibleProducers/data/set', payload: responsibleProducers });
      dispatch({ type: 'responsibilities/set', payload: responsibilities });
    } finally {
      dispatch({ type: 'status/set', payload: 'ready' });
    }
  }

  async function saveResponsibilities() {
    dispatch({ type: 'status/set', payload: 'saving' });

    const responsibilities: PartnerEshopResponsibilities = {
      ...state.responsibilities,
      defaultResponsiblePersonId: state.edit.responsiblePersonId ?? undefined,
      defaultResponsibleProducerId: state.edit.responsibleProducerId ?? undefined,
    };

    try {
      await savePartnerEshopResponsibilities(marketplaceCode, responsibilities);
      dispatch({ type: 'responsibilities/set', payload: responsibilities });
      showSuccessMessage('Responsibilities successfully saved.');
    } finally {
      dispatch({ type: 'status/set', payload: 'ready' });
    }
  }

  useEffect(() => {
    fetchData().then();
  }, [eshop]);

  function handleEditResponsiblePerson(editingResponsiblePerson?: AllegroResponsiblePerson) {
    dispatch({ type: 'responsiblePersons/edit/open', payload: editingResponsiblePerson });
  }

  async function handleImportResponsiblePersons(file: File) {
    await uploadResponsiblePersonsExcel(marketplaceCode, file);
    await fetchData();
  }

  async function handleExportResponsiblePersons() {
    await downloadResponsiblePersonsExcel(state.responsiblePersons.data);
  }

  function handleResponsiblePersonClosed() {
    dispatch({ type: 'responsiblePersons/edit/close' });
  }

  function handleResponsiblePersonSaved() {
    dispatch({ type: 'responsiblePersons/edit/close' });
    fetchData().then();
  }

  function handleEditResponsibleProducer(editingResponsibleProducer?: AllegroResponsibleProducer) {
    dispatch({ type: 'responsibleProducers/edit/open', payload: editingResponsibleProducer });
  }

  async function handleImportResponsibleProducers(file: File) {
    await uploadResponsibleProducersExcel(marketplaceCode, file);
    await fetchData();
  }

  async function handleExportResponsibleProducers() {
    await downloadResponsibleProducersExcel(state.responsibleProducers.data);
  }

  function handleResponsibleProducerClosed() {
    dispatch({ type: 'responsibleProducers/edit/close' });
  }

  function handleResponsibleProducerSaved() {
    dispatch({ type: 'responsibleProducers/edit/close' });
    fetchData().then();
  }

  function handleSave() {
    saveResponsibilities().then();
  }

  function handleCancel() {
    dispatch({ type: 'edit/cancel' });
  }

  if (state.status === 'loading') {
    return <Loader />;
  }

  return (
    <>
      <Form
        layout="horizontal"
        formValue={state.edit}
        onChange={(edit) => dispatch({ type: 'edit/set', payload: edit as Edit })}
      >
        <Form.Group>
          <Form.ControlLabel>Responsible person</Form.ControlLabel>
          <Stack spacing={20}>
            <Form.Control
              name="responsiblePersonId"
              accepter={SelectPickerWithActions}
              data={state.responsiblePersons.data}
              valueKey="id"
              labelKey="name"
              cleanable={false}
              renderMenuItem={(label: ReactNode, item: any) => (
                <SelectOptionWithActions label={label}>
                  <IconButton
                    size="xs"
                    icon={<EditIcon />}
                    appearance="subtle"
                    onClick={
                    () => handleEditResponsiblePerson(item as AllegroResponsiblePerson)
                  }
                  />
                </SelectOptionWithActions>
              )}
            />
            <Button appearance="primary" onClick={() => handleEditResponsiblePerson()}>
              Create new responsible person
            </Button>
            <FilePicker
              accept=".csv,.xlsx,.xls"
              onPick={(file) => handleImportResponsiblePersons(file)}
            >
              <Button appearance="primary">
                Import
              </Button>
            </FilePicker>
            <Button appearance="primary" onClick={() => handleExportResponsiblePersons()}>
              Export
            </Button>
          </Stack>
        </Form.Group>
        <Form.Group>
          <Form.ControlLabel>Responsible producer</Form.ControlLabel>
          <Stack spacing={20}>
            <Form.Control
              name="responsibleProducerId"
              accepter={SelectPickerWithActions}
              data={state.responsibleProducers.data}
              valueKey="id"
              labelKey="name"
              cleanable={false}
              renderMenuItem={(label: ReactNode, item: any) => (
                <SelectOptionWithActions label={label}>
                  <IconButton
                    size="xs"
                    icon={<EditIcon />}
                    appearance="subtle"
                    onClick={
                    () => handleEditResponsibleProducer(item as AllegroResponsibleProducer)
                  }
                  />
                </SelectOptionWithActions>
              )}
            />
            <Button appearance="primary" onClick={() => handleEditResponsibleProducer()}>
              Create new responsible producer
            </Button>
            <FilePicker
              accept=".csv,.xlsx,.xls"
              onPick={(file) => handleImportResponsibleProducers(file)}
            >
              <Button appearance="primary">
                Import
              </Button>
            </FilePicker>
            <Button appearance="primary" onClick={() => handleExportResponsibleProducers()}>
              Export
            </Button>
          </Stack>
        </Form.Group>
        <Form.Group>
          <ButtonToolbar>
            <Button
              appearance="primary"
              onClick={() => handleSave()}
              loading={state.status === 'saving'}
              type="submit"
            >
              Save
            </Button>
            <Button appearance="ghost" onClick={() => handleCancel()}>
              Cancel
            </Button>
          </ButtonToolbar>
        </Form.Group>
      </Form>
      <EshopSetupAllegroResponsiblePersonEditModal
        isOpen={state.responsiblePersons.isEditing}
        marketplaceCode={marketplaceCode}
        responsiblePerson={state.responsiblePersons.editingResponsiblePerson}
        onClose={() => handleResponsiblePersonClosed()}
        onSaved={() => handleResponsiblePersonSaved()}
      />
      <EshopSetupAllegroResponsibleProducerEditModal
        isOpen={state.responsibleProducers.isEditing}
        marketplaceCode={marketplaceCode}
        responsibleProducer={state.responsibleProducers.editingResponsibleProducer}
        onClose={() => handleResponsibleProducerClosed()}
        onSaved={() => handleResponsibleProducerSaved()}
      />
    </>
  );
}
